import { create } from 'zustand';
import { markManualOrgNav, clearManualOrgNav } from './orgSwitchNav';

// Reactive signal for an in-flight organization switch.
//
// The switch handlers live deep in the tree (NavSidebar's org menu / "Back to
// Consultant"), but the full-screen switch loader is rendered by OrgBoundary,
// an ancestor. This store is the channel between them: a handler flips
// `isSwitching` the MOMENT the user clicks — before `setActive()` has even
// resolved — so OrgBoundary can:
//   1. show the loader immediately (instant feedback that the switch started),
//      instead of waiting for Clerk's listener to fire after the round-trip; and
//   2. unmount the route tree for the whole transition, so no page guard renders
//      against the transient stale/cache-cleared org state mid-switch (that
//      window is what used to bounce the user to /init or /not-found).
//
// OrgBoundary owns clearing it — it calls `endSwitch()` once the token refresh,
// cache clear and remount have settled onto the new org. Complements
// [orgSwitchNav.js](./orgSwitchNav.js), which handles WHERE a switch navigates;
// this one handles the loading feedback around it.
export const useOrgSwitchStore = create((set) => ({
  isSwitching: false,
  beginSwitch: () => set({ isSwitching: true }),
  endSwitch: () => set({ isSwitching: false }),
}));

// The one way to switch the active Clerk organization from a UI handler.
//
// Owns the whole choreography so call sites can't desynchronize it:
//   - same-org guard: if the target is already active, skip the switch
//     machinery entirely (calling setActive for the active org emits no
//     org-id change, so OrgBoundary's applyOrg would early-return and the
//     loader would never be cleared — a stuck fullscreen loader). The guard
//     reads Clerk's authoritative `window.Clerk.organization` first because
//     the `useOrganization()` hook is documented (see OrgBoundary) to lag
//     behind same-session switches; `activeOrgId` is the caller's fallback.
//   - loader pairing: beginSwitch() before setActive for instant feedback;
//     endSwitch() on failure (OrgBoundary's applyOrg ends it on success).
//   - manual-nav claim: when the caller navigates itself (`navigate`), the
//     markManualOrgNav()/clearManualOrgNav() pairing is handled here so
//     OrgBoundary skips its default overview redirect — and a failed switch
//     can't leak the claim into the next unrelated switch. Pass `navigateTo`
//     (the destination path the `navigate` thunk pushes to) so OrgBoundary can
//     hold the switch loader up until the router actually reaches it, closing
//     the same old-route-vs-new-org flash the sidebar switch avoids. Omitting
//     it still works — OrgBoundary just can't gate on a path and drops the
//     loader once the switch settles.
//
// Returns true when the switch (or the already-active shortcut) completed,
// false when setActive failed.
export const performOrgSwitch = async ({ setActive, orgId, activeOrgId, navigate, navigateTo }) => {
  if (!orgId) return false;
  const currentOrgId = window.Clerk?.organization?.id ?? activeOrgId ?? null;
  if (orgId === currentOrgId) {
    // Already the active org — no switch to perform. Still run the caller's
    // navigation (e.g. a stale "Back to Consultant" while the consultant org
    // is already active should land on the overview, not dead-end).
    navigate?.();
    return true;
  }
  const { beginSwitch, endSwitch } = useOrgSwitchStore.getState();
  beginSwitch();
  // Record the manual-nav claim carrying the destination path (or a bare `true`
  // when the caller navigates but didn't tell us where). Must precede setActive
  // so OrgBoundary's applyOrg — which fires on Clerk's emission during
  // setActive — sees the claim.
  if (navigate) markManualOrgNav(navigateTo ?? true);
  try {
    await setActive({ organization: orgId });
    navigate?.();
    return true;
  } catch (err) {
    if (navigate) clearManualOrgNav();
    endSwitch();
    console.error('Failed to switch organization:', err);
    return false;
  }
};
